import { QueryList } from '@angular/core';

import { Util } from '../../chart/util';
import {
    Cell,
    CellNode,
    Column,
    Context,
    DivRendererOptions,
    GridContent,
    Row,
    RowNode,
    SelectTemplateFn,
    Sort,
    SortOrder,
    sortOrderWithDefault,
} from '../../grid';
import { PerformerItemView } from '../../main/model/performer-item-view.model';
import { tableColumns } from '../fixture/table-columns';
import { ColumnId } from '../model/column-id.model';
import { SortOptions } from '../model/sort-options.model';
import { TableData } from '../model/table-data.model';
import { TemplateId } from '../model/template-id.model';

function selectTpl(): SelectTemplateFn {
    return (
        context: Context,
        contents: QueryList<GridContent>,
    ): GridContent => {
        if (context.t === 'grid') {
            return contents.find((e) => e.type === 'grid');
        }
        if (context.t === 'row') {
            return contents.find((e) => {
                if (e.type !== 'row') {
                    return false;
                }
                let templateId = context.ctx?.templateId;
                if (templateId == null) {
                    templateId =
                        context.r?.cells?.children?.[0].def?.ctx?.templateId ??
                        'header';
                }
                return e.id === templateId;
            });
        }
        if (context.t === 'cell') {
            return contents.find(
                (e) => e.type === 'cell' && e.id === context.ctx.templateId,
            );
        }
    };
}

function compareFn(
    cellA: CellNode,
    cellB: CellNode,
    rowA: RowNode,
    rowB: RowNode,
    def: Sort,
): number {
    return rowA.def.ctx.index - rowB.def.ctx.index;
}

function injectLines(lines: string[], el: HTMLElement): void {
    lines.forEach((line, li) => {
        el.appendChild(document.createTextNode(line));
        if (li !== lines.length - 1) {
            el.appendChild(document.createElement('br'));
        }
    });
}

function wrapText(el: HTMLElement): void {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    const textEl = el.querySelector(
        '.cmps-mi2-table--row--text',
    ) as HTMLElement;
    if (textEl != null) {
        // TODO handle html better
        const text = textEl.innerHTML.replace(/(<br>|&nbsp;)/gi, ' ');
        const lines = Util._wrapText(text, 2, text.length / 2);
        textEl.textContent = '';
        injectLines(lines, textEl);
    }
}

function unwrapText(el: HTMLElement): void {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    const textEl = el.querySelector(
        '.cmps-mi2-table--row--text',
    ) as HTMLElement;
    if (textEl != null) {
        // TODO handle html better
        const text = textEl.innerHTML.replace(/(<br>|&nbsp;)/gi, ' ');
        textEl.textContent = '';
        textEl.appendChild(document.createTextNode(text));
    }
}

const excludeMeasure: string[] = [
    ColumnId.MI2_SCORE,
    ColumnId.OA_SCORE,
    ColumnId.MI2_PLUS_SCORE,
    ColumnId.SCORECARD_POINTS,
    ColumnId.PREVIOUS_SCORECARD_POINTS,
    ColumnId.IMPROVEMENT_ACHIEVED,
];

function willMeasure(node: CellNode): void {
    if (node.renderContext?.element != null) {
        node.renderContext.element.classList.add('cmps-mi2-table--measure');
        if (
            node.context?.r?.context?.ctx?.templateId !== TemplateId.CONTENT ||
            !excludeMeasure.includes(node.context?.c?.id)
        ) {
            wrapText(node.renderContext.element);
        }
    }
}

function didMeasure(node: CellNode): void {
    if (node.renderContext?.element != null) {
        node.renderContext.element.classList.remove('cmps-mi2-table--measure');
        if (
            node.context?.r?.context?.ctx?.templateId !== TemplateId.CONTENT ||
            !excludeMeasure.includes(node.context?.c?.id)
        ) {
            unwrapText(node.renderContext.element);
        }
    }
}

function sliceColumns(start: number, end?: number): Column[] {
    return tableColumns.slice(start, end).map<Column>((e) => {
        return {
            id: e.id,
            width: 80,
            compareFn,
            sticky: e.id === ColumnId.PERFORMER ? 'left' : undefined,
            ctx: {
                templateId: TemplateId.HEADER,
                data: e,
            },
            willMeasure,
            didMeasure,
        };
    });
}

export function createTableData(
    data: PerformerItemView[],
    sortOptions: SortOptions,
): TableData {
    const columns: Column[] = [
        {
            id: 'bundle-left',
            // width: 80,
            sticky: 'left',
            // cellSpan: 1,
            ctx: {
                templateId: TemplateId.BUNDLE,
            },
            children: sliceColumns(0, 1),
        },
        {
            id: 'bundle-a',
            // width: 80,
            // cellSpan: 1,
            ctx: {
                templateId: TemplateId.BUNDLE,
            },
            children: sliceColumns(1, 2),
        },
        {
            id: 'bundle-b',
            // width: 80,
            // cellSpan: 3,
            ctx: {
                templateId: TemplateId.BUNDLE,
            },
            children: sliceColumns(2, 5),
        },
        {
            id: 'bundle-c',
            // width: 80,
            // cellSpan: 2,
            ctx: {
                templateId: TemplateId.BUNDLE,
            },
            children: sliceColumns(5, 7),
        },
        {
            id: 'bundle-d',
            // width: 80,
            // cellSpan: 2,
            ctx: {
                templateId: TemplateId.BUNDLE,
            },
            children: sliceColumns(7, 9),
        },
        {
            id: 'bundle-e',
            // width: 80,
            // cellSpan: 2,
            ctx: {
                templateId: TemplateId.BUNDLE,
            },
            children: sliceColumns(9),
        },
    ];

    const flatColumns = columns.reduce<Column[]>((p, c) => {
        return p.concat(c.children);
    }, []);

    const rendererOpts: DivRendererOptions = {
        columns,
        defaultRenderFn: null,
        height: 500,
        horizontalScrollPadding: 9999,
        verticalScrollPadding: 9999,
    };

    const rows =
        data?.map<Row>((r, ri) => {
            const cells = flatColumns.map<Cell>((e) => {
                return {
                    columnId: e.id,
                    height: 30,
                    ctx: {
                        templateId: TemplateId.CONTENT,
                        data: r,
                    },
                    willMeasure,
                    didMeasure,
                };
            });

            return {
                id: `${ri}`,
                height: 30,
                cells,
                ctx: {
                    templateId: TemplateId.CONTENT,
                    index: ri,
                },
            };
        }) ?? [];

    const defaultSortDef: Sort = {
        columnId: ColumnId.PERFORMER,
        order: SortOrder.ASC,
    };
    const sortDefs = sortOrderWithDefault(
        flatColumns,
        null,
        {
            columnId: flatColumns.find(
                (e) => e.ctx.data.id === sortOptions.columnId,
            ).id,
            order:
                sortOptions.order === 'desc' ? SortOrder.DESC : SortOrder.ASC,
        },
        defaultSortDef,
    );

    return {
        rendererOpts,
        selectTpl: selectTpl(),
        rows,
        sortDefs,
    };
}
